import React from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HeroCarousel = () => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    fade: true,
    cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    pauseOnHover: false,
    arrows: false
  };

  const slides = [
    {
      id: 1,
      image: '/images/hero-factory.png',
      title: 'Manufacturing Excellence',
      subtitle: 'State-of-the-art cable production facility'
    },
    {
      id: 2,
      image: '/images/hero-office.png',
      title: 'Professional Infrastructure',
      subtitle: 'Corporate office in Sector-57, Noida'
    },
    {
      id: 3,
      image: '/images/hero-reception.png',
      title: 'Modern Workspace',
      subtitle: 'Premium reception and branding'
    },
    {
      id: 4,
      image: '/images/hero-factory.png',
      title: 'Quality Production',
      subtitle: 'Dedicated team committed to excellence'
    }
  ];

  return (
    <div className="relative w-full" data-testid="hero-carousel">
      <Slider {...settings}>
        {slides.map((slide) => (
          <div key={slide.id} className="relative">
            {/* Use min-h with auto height for mobile flexibility */}
            <div className="relative min-h-[500px] sm:min-h-[550px] md:min-h-[650px] lg:min-h-[700px] w-full">
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ 
                  backgroundImage: `url(${slide.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center center'
                }}
              />
              
              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
              
              {/* Content */}
              <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center py-8 sm:py-12">
                <div className="max-w-3xl animate-fade-in-up">
                  {/* Company Name - Smaller on mobile */}
                  <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black text-white mb-2 sm:mb-4 tracking-tight leading-tight">
                    RSD Accessories
                  </h1>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 tracking-tight leading-tight">
                    <span className="text-white">Pvt. </span>
                    <span className="text-white">Ltd.</span>
                  </h2>
                  
                  {/* Brand Line - Smaller padding on mobile */}
                  <div className="inline-block bg-[#FF4D00] px-4 sm:px-6 py-2 sm:py-3 rounded-lg mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-white">
                      RSD – A Brand of New Eon
                    </h3>
                  </div>
                  
                  {/* Tagline - Smaller on mobile */}
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white font-medium mb-2 sm:mb-4">
                    Premium Quality Mobile Accessories
                  </p>
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl text-neutral-300 mb-4 sm:mb-6 md:mb-8">
                    Manufactured in India for Modern Lifestyles
                  </p>
                  
                  {/* Slide-specific text - Hidden on very small screens */}
                  <p className="hidden sm:block text-base md:text-lg lg:text-xl text-[#FF4D00] font-semibold mb-6 md:mb-8">
                    {slide.title} • {slide.subtitle}
                  </p>
                  
                  {/* CTA Buttons - Smaller on mobile */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Link to="/shop">
                      <Button
                        data-testid="hero-shop-now-btn"
                        className="btn-primary bg-[#FF4D00] hover:bg-[#E04400] text-white rounded-full px-6 sm:px-8 md:px-10 py-5 sm:py-6 md:py-7 text-base sm:text-lg md:text-xl font-bold shadow-2xl w-full sm:w-auto"
                      >
                        Shop Now
                        <ArrowRight className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
                      </Button>
                    </Link>
                    <Link to="/become-distributor">
                      <Button
                        data-testid="hero-distributor-btn"
                        className="bg-black hover:bg-neutral-900 text-white border-2 border-white rounded-full px-6 sm:px-8 md:px-10 py-5 sm:py-6 md:py-7 text-base sm:text-lg md:text-xl font-bold shadow-2xl w-full sm:w-auto"
                      >
                        Become a Distributor
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </Slider>
      
      {/* Custom Dots Styling */}
      <style>{`
        .slick-dots {
          bottom: 20px;
        }
        @media (min-width: 640px) {
          .slick-dots {
            bottom: 30px;
          }
        }
        .slick-dots li button:before {
          font-size: 10px;
        }
        @media (min-width: 640px) {
          .slick-dots li button:before {
            font-size: 12px;
          }
        }
        .slick-dots li button:before {
          color: white;
          opacity: 0.5;
        }
        .slick-dots li.slick-active button:before {
          color: #FF4D00;
          opacity: 1;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default HeroCarousel;
